import './reset.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Route, Routes } from 'react-router-dom';
import Dashboard from '@/pages/dashboard/Dashboard.tsx';
import Users from '@/pages/users/Users.tsx';
import Login from '@/pages/auth/Login.tsx';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PrivateRoute from '@/component/util/PrivateRoute.tsx';
import Account from '@/pages/account/Account.tsx';
import { GOOGLE_MAPS_API_KEY } from '@/constant/constant.ts';
import HomeRoute from '@/component/util/HomeRoute.tsx';
import Home from '@/pages/landing/Home/Home.tsx';
import { Suspense, useEffect, useMemo } from 'react';
import { useLoginStore } from '@/store/LoginStore.ts';
import UserDetailMapPage from '@/pages/users/detail/UserDetailMapPage.tsx';
import DevPage from '@/pages/etc/DevPage.tsx';
import NotificationPage from '@/pages/notification/NotificationPage.tsx';
import Loader from '@/pages/etc/Loader/Loader.tsx';
import { useTranslation } from 'react-i18next';
import { LoadScript } from '@react-google-maps/api';

function App() {
    const { t, i18n } = useTranslation();
    const queryClient = new QueryClient();
    const { accessToken } = useLoginStore();
    const wrapPrivateRoute = (element: any, href = '/login') => (
        <PrivateRoute href={href}>{element}</PrivateRoute>
    );
    
    // 현재 언어에 따라 Google Maps region 설정
    const mapsRegionConfig = useMemo(() => {
        const currentLang = i18n.language;
        let region = 'KR';
        let language = currentLang;
        
        switch (currentLang) {
            case 'ko':
                region = 'KR';
                break;
            case 'en':
                region = 'US';
                break;
            case 'ja':
                region = 'JP';
                break;
            default:
                region = 'KR';
                break;
        }
        
        return { region, language };
    }, [i18n.language]);

    useEffect(() => {
        return () => {
            queryClient.removeQueries();
        };
    }, []);

    // 토큰 갱신 로직은 api.ts에서 처리하므로 이 useEffect는 제거

    useEffect(() => {
        document.title = t('app.title.clomental');
    }, [t]);

    return (
        <>
            <Suspense>
                <LoadScript 
                    googleMapsApiKey={GOOGLE_MAPS_API_KEY} 
                    region={mapsRegionConfig.region} 
                    language={mapsRegionConfig.language}
                >
                    <div className="font-sans">
                        <ToastContainer
                            position="bottom-right"
                            autoClose={3000}
                            newestOnTop={false}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss={false}
                            draggable
                            pauseOnHover
                            theme="light"
                            stacked
                        />
                        <QueryClientProvider client={queryClient}>
                            {accessToken && <Loader />}
                            <Routes>
                                <Route path="/" element={<HomeRoute />} />
                                <Route path="/home" element={<Home />} />
                                <Route path="/dashboard" element={wrapPrivateRoute(<Dashboard />)} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/user" element={wrapPrivateRoute(<Users />)} />
                                <Route path="/user/:id/map" element={wrapPrivateRoute(<UserDetailMapPage />)} />
                                <Route path="/notification" element={wrapPrivateRoute(<NotificationPage />)} />
                                <Route path="/account" element={wrapPrivateRoute(<Account />)} />
                                <Route path="/dev" element={wrapPrivateRoute(<DevPage />)} />
                            </Routes>
                        </QueryClientProvider>
                    </div>
                </LoadScript>
            </Suspense>
        </>
    );
}

export default App;